import type { FeatureFlags } from "../../../config/featureFlags";
import type { cacheableData } from "../../workbook/cache";
import { getEstimateReportData } from "../../workbook";
import { fetchEstimateExport } from "../../../api/estimates";
import { transformUseGroups } from "../../datasources/useGroups";
import { getProcessedEstimate } from "../../datasources/estimate";
import { formatDisplayValue } from "../../utils/displayValue";
import { CROSS_TAB_FLOOR_GROUPS_VALUE, crossTabSortFieldOptionValue, mfufLabel, mfufLevelsOf } from "./crossTab";
import { columnNames } from "./rangeNames";
import type { ReportOptionDefinition, ReportOptionDefinitionCheckbox, ReportOptionDefinitionDropdown } from "../api";

type DropdownOptionsConfig = {
  multiple?: boolean;
  disabledWhen?: (o: cacheableData) => boolean;
  requiredNamedRanges?: string[];
  emptyOptionLabel: string;
  valueKey: string;
  labelKey: string;
  getDropdownOptions: () => Promise<Record<string, unknown>[]>;
};

type CheckboxOptionsConfig = {
  featureFlag?: keyof FeatureFlags;
  disabledWhen?: (o: cacheableData) => boolean;
  requiredNamedRanges?: string[];
  /** Overrides the shared label when one report gives the key a narrower meaning. */
  label?: string;
};

const readableOptions: Record<string, string> = {
  // groupBy: "Group By",
  // useFormulas: "Use Formulas",
  noGrouping: "No Grouping",
  groupRows: "Group Rows",
  subtotalsInHeader: "Subtotals in Header",
  splitByUse: "Split By Building/Use",
  includeAssemblyItems: "Show Assembly Items",
  freezeGroupLevels: "Freeze Group Levels",
  onlyLevelOneSubtotals: "Hide Subtotals",
  allocateMarkups: "Allocate Markups",
  filterByUseGroup: "Filter by Use Group",
  filterByAlternate: "Filter by VM Decisions",
  filterByType: "Filter by Type",
  showCodes: "Show Codes",
  showCodesColumns: "Show Codes (Columns)",
  hideLineItemDetails: "Hide Line Item Details (SOV)",
  hideZeroCostItems: "Hide Zero Cost Items",
  includeFeatures: "Include Features",
  excludeAccepted: "Exclude Accepted",
  excludeRejected: "Exclude Rejected",
  crossTabColumns: "Cross Tab Columns",
};

const optionAsHumanReadable = (option: string): string => readableOptions[option] ?? option;

/**
 * Names the Cross Tab's column block can have: `\cross_tab` on current sheets, or the legacy names
 * on sheets templated before the Cross Tab Columns option, which the generator renames on first run.
 */
const crossTabBlockNames = [columnNames.crossTab, columnNames.useGroups, columnNames.floorGroups].map(
  (name) => `\\${name}`
);

const isNoGrouping = (o: cacheableData) => !!o.noGrouping;

const noGroupingDependentCheckboxes: Partial<Record<string, CheckboxOptionsConfig>> = {
  groupRows: { disabledWhen: isNoGrouping },
  subtotalsInHeader: { disabledWhen: isNoGrouping },
  onlyLevelOneSubtotals: { disabledWhen: isNoGrouping },
  freezeGroupLevels: { disabledWhen: isNoGrouping },
  splitByUse: { disabledWhen: isNoGrouping },
};

/** The checkboxes of a report whose sheets can carry a `\cross_tab` block. */
const crossTabCheckboxes: Partial<Record<string, CheckboxOptionsConfig>> = {
  // Only Cross Tab sheets have the column headers this option labels
  showCodesColumns: { requiredNamedRanges: crossTabBlockNames },
  showCodes: { label: "Show Codes (Rows)" },
};

const VMTypeDropdownConfig = (): DropdownOptionsConfig => ({
  multiple: false,
  emptyOptionLabel: "No Filter - Show All Types",
  valueKey: "value",
  labelKey: "label",
  getDropdownOptions: async () => {
    const estimateId = (await getEstimateReportData())[0]?.estimate_id as string | undefined;
    if (!estimateId) return [];
    const result = await fetchEstimateExport(estimateId);
    if (!result.success || !result.data) return [];
    const { alternates, line_items } = result.data;
    const altIdsWithItems = new Set(line_items.filter((i) => i.alternate).map((i) => i.alternate));
    const uniqueTypes = Array.from(
      new Set(
        (alternates || [])
          .filter((alt) => altIdsWithItems.has(alt.id))
          .map((alt) => String(alt.type ?? "").trim())
          .filter((t) => t.length > 0)
      )
    ).sort((a, b) => a.localeCompare(b));
    return uniqueTypes.map((type) => ({ value: type, label: formatDisplayValue(type) }));
  },
});

const VMDecisionDropdownConfig = (): DropdownOptionsConfig => ({
  multiple: true,
  emptyOptionLabel: "No Filter - Show All VM Decisions",
  valueKey: "id",
  labelKey: "label",
  getDropdownOptions: async () => {
    const estimateId = (await getEstimateReportData())[0]?.estimate_id as string | undefined;
    if (!estimateId) return [];
    const result = await fetchEstimateExport(estimateId);
    if (!result.success || !result.data) return [];
    const { alternates, line_items } = result.data;
    const altIdsWithItems = new Set(line_items.filter((i) => i.alternate).map((i) => i.alternate));
    return (alternates || [])
      .filter((alt) => altIdsWithItems.has(alt.id))
      .map((alt) => ({ id: alt.id, label: alt.label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  },
});

/** Cross Tab Columns lists the dimensions that can fill a `\cross_tab` block; use groups is the default. */
const crossTabColumnsDropdownConfig = (): DropdownOptionsConfig => ({
  multiple: false,
  // Only Cross Tab sheets have the block this option fills
  requiredNamedRanges: crossTabBlockNames,
  emptyOptionLabel: "Use Groups",
  valueKey: "value",
  labelKey: "label",
  getDropdownOptions: async () => {
    const estimateId = (await getEstimateReportData())[0]?.estimate_id as string | undefined;
    if (!estimateId) return [];
    const processed = await getProcessedEstimate(estimateId);
    if (!processed) return [];
    const choices: { value: string; label: string }[] = [];
    if ((processed.estimateData.floor_groups || []).length > 0) {
      choices.push({ value: CROSS_TAB_FLOOR_GROUPS_VALUE, label: "Buildings" });
    }
    choices.push(...mfufLevelsOf(processed.baseBidItems).map((key) => ({ value: key, label: mfufLabel(key) })));
    const sortFields = Object.values(processed.usedSortFields).sort((a, b) => a.name.localeCompare(b.name));
    choices.push(...sortFields.map((sf) => ({ value: crossTabSortFieldOptionValue(sf.id), label: sf.name })));
    return choices;
  },
});

function splitOptionKeys(options: Record<string, unknown>): {
  checkboxKeys: string[];
  dropdownKeys: string[];
} {
  const checkboxKeys: string[] = [];
  const dropdownKeys: string[] = [];
  for (const key of Object.keys(options)) {
    const v = options[key];
    if (typeof v === "boolean") checkboxKeys.push(key);
    else if (typeof v === "string") dropdownKeys.push(key);
    else if (Array.isArray(v) && v.every((item) => typeof item === "string")) dropdownKeys.push(key);
  }
  return { checkboxKeys, dropdownKeys };
}

function checkboxes(
  keys: string[],
  configByKey?: Partial<Record<string, CheckboxOptionsConfig>>
): ReportOptionDefinitionCheckbox[] {
  return keys.map((key) => ({
    controlType: "checkbox",
    key,
    label: optionAsHumanReadable(key),
    ...configByKey?.[key],
  }));
}

function dropdowns(
  keys: string[],
  configByKey: Record<string, DropdownOptionsConfig>
): ReportOptionDefinitionDropdown[] {
  return keys.map((key) => ({
    controlType: "dropdown",
    key,
    label: optionAsHumanReadable(key),
    ...configByKey[key],
  }));
}

function optionDefinitionsFromDefaults(
  defaultOptions: Record<string, unknown>,
  config?: {
    checkboxConfigByKey?: Partial<Record<string, CheckboxOptionsConfig>>;
    dropdownConfigByKey?: Record<string, DropdownOptionsConfig>;
  }
): {
  checkboxes: ReportOptionDefinitionCheckbox[];
  dropdowns: ReportOptionDefinitionDropdown[];
} {
  const { checkboxKeys, dropdownKeys } = splitOptionKeys(defaultOptions);
  const checkboxesDefs = checkboxes(checkboxKeys, config?.checkboxConfigByKey ?? {});
  const dropdownsDefs = dropdowns(dropdownKeys, config?.dropdownConfigByKey ?? {});
  return { checkboxes: checkboxesDefs, dropdowns: dropdownsDefs };
}

export function getSimpleCheckboxDefinitions(defaultOptions: Record<string, unknown>): ReportOptionDefinition[] {
  const { checkboxes: c } = optionDefinitionsFromDefaults(defaultOptions);
  return c;
}

export function getMarkupsOptionDefinitions(defaultOptions: Record<string, unknown>): ReportOptionDefinition[] {
  const { checkboxes: c } = optionDefinitionsFromDefaults(defaultOptions, {
    checkboxConfigByKey: noGroupingDependentCheckboxes,
  });
  return c;
}

export function getEstimateOptionDefinitions(defaultOptions: Record<string, unknown>): ReportOptionDefinition[] {
  const { checkboxes: c, dropdowns: d } = optionDefinitionsFromDefaults(defaultOptions, {
    checkboxConfigByKey: {
      ...noGroupingDependentCheckboxes,
      ...crossTabCheckboxes,
      splitByUse: { disabledWhen: (o) => !!(o.filterByUseGroup as string) || isNoGrouping(o) },
      hideLineItemDetails: { disabledWhen: isNoGrouping },
    },
    dropdownConfigByKey: {
      crossTabColumns: crossTabColumnsDropdownConfig(),
      filterByUseGroup: {
        multiple: false,
        emptyOptionLabel: "No Filter - Show All Use Groups",
        valueKey: "id",
        labelKey: "name",
        getDropdownOptions: async () => {
          const estimateId = (await getEstimateReportData())[0]?.estimate_id as string | undefined;
          if (!estimateId) return [];
          const result = await fetchEstimateExport(estimateId);
          if (!result.success || !result.data) return [];
          const floorGroups = result.data.floor_groups || [];
          const useGroupStats = result.data.estimate?.stats?.useGroups || [];
          return transformUseGroups(useGroupStats, floorGroups).map((group) => ({
            id: group.id,
            name: group.name,
          }));
        },
      },
    },
  });
  return [...c, ...d];
}

export function getEstimateSOVOptionDefinitions(defaultOptions: Record<string, unknown>): ReportOptionDefinition[] {
  const { checkboxes: c, dropdowns: d } = optionDefinitionsFromDefaults(defaultOptions, {
    checkboxConfigByKey: crossTabCheckboxes,
    dropdownConfigByKey: { crossTabColumns: crossTabColumnsDropdownConfig() },
  });
  return [...c, ...d];
}

export function getAlternatesEstimateOptionDefinitions(
  defaultOptions: Record<string, unknown>
): ReportOptionDefinition[] {
  const { checkboxes: c, dropdowns: d } = optionDefinitionsFromDefaults(defaultOptions, {
    checkboxConfigByKey: crossTabCheckboxes,
    dropdownConfigByKey: {
      filterByAlternate: VMDecisionDropdownConfig(),
      filterByType: VMTypeDropdownConfig(),
      crossTabColumns: crossTabColumnsDropdownConfig(),
    },
  });
  return [...c, ...d];
}

export function getAlternatesOptionDefinitions(defaultOptions: Record<string, unknown>): ReportOptionDefinition[] {
  const { checkboxes: c, dropdowns: d } = optionDefinitionsFromDefaults(defaultOptions, {
    checkboxConfigByKey: {
      showCodes: { label: "Show Codes (Rows)" },
    },
    dropdownConfigByKey: {
      filterByType: VMTypeDropdownConfig(),
    },
  });
  return [...c, ...d];
}
